import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useCallback } from 'react';
import styled from 'styled-components';

import NewItemCardRegisterLink from '@/components/GridItemCard/Card/components/NewItemCardRegisterLink/NewItemCardRegisterLink';
import PlaceBidButton from '@/components/GridItemCard/Card/components/PlaceBidButton/PlaceBidButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getIsRegistered,
    getRegistrationApproved,
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryCatalogId,
    getItemSummaryCatalogOnly,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { openRetractBidModal } from '@/redux/modules/modal';

export type CardRegistrationProps = {
    isMobile: boolean;
    itemId: number;
};

const CardRegistration = ({ isMobile, itemId }: CardRegistrationProps) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryCatalogOnly(state, itemId));
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));

    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));

    const openRetractBidModalCallback = useCallback(() => {
        dispatch(openRetractBidModal(itemId));
    }, [dispatch, itemId]);

    return (
        <RegisterSection data-testid="myItemsCardRegistration">
            {!declined && !pending && !isCatalogOnly && <PlaceBidButton itemId={itemId} />}
            {(!approved || isCatalogOnly) && (
                <NewItemCardRegisterLink
                    catalogId={catalogId}
                    itemId={itemId}
                    registerToBidMessageId="registration.register_for_auction_my_items"
                />
            )}
            {(approved || registered) && bidderHasBid && isMobile && !pending && (
                <StyledButton
                    aria-label="retract bid button"
                    onClick={openRetractBidModalCallback}
                    size="sm"
                >
                    {formatMessage({ id: 'retract' })}
                </StyledButton>
            )}
        </RegisterSection>
    );
};

export default CardRegistration;

const RegisterSection = styled.section`
    display: flex;
    grid-area: registerForAuction;
    //prevent grid section height from stretching vertically
    max-height: 34px;
`;

const StyledButton = styled(Button)`
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding-left: 14px;
        padding-right: 14px;
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        padding-left: 9px;
        padding-right: 9px;
    }
`;
